import { useState, useEffect } from 'react';
import { useArtifactStore } from '../hooks/useArtifactStore';
import type { WidgetProps } from './WidgetRegistry';
import type { SqlContent } from '../services/api';

interface SchemaNode {
  schema: string;
  tables: Map<string, string[]>;
}

export function DataSourceExplorerWidget(_props: WidgetProps) {
  const { artifacts, loadArtifacts } = useArtifactStore();
  const [expandedSchema, setExpandedSchema] = useState<string | null>(null);
  const [expandedTable, setExpandedTable] = useState<string | null>(null);

  useEffect(() => { loadArtifacts('sql'); }, []);

  const schemas = new Map<string, SchemaNode>();
  for (const a of artifacts) {
    if (a.content.type !== 'sql') continue;
    const sqlContent = a.content as SqlContent;
    for (const table of sqlContent.tables) {
      const parts = table.split('.');
      const schemaName = parts.length > 1 ? parts[0] : 'default';
      const tableName = parts.length > 1 ? parts[1] : parts[0];

      if (!schemas.has(schemaName)) {
        schemas.set(schemaName, { schema: schemaName, tables: new Map() });
      }
      const node = schemas.get(schemaName)!;
      if (!node.tables.has(tableName)) {
        node.tables.set(tableName, []);
      }
      for (const col of sqlContent.outputColumns) {
        const cols = node.tables.get(tableName)!;
        if (!cols.includes(col.name)) cols.push(col.name);
      }
    }
  }

  const schemaList = [...schemas.values()].sort((a, b) => a.schema.localeCompare(b.schema));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-tower-border/50 shrink-0">
        <div className="text-xs text-tower-muted font-semibold">Schema Explorer</div>
        <div className="text-[10px] text-tower-muted">
          {schemaList.length} schemas · {[...schemas.values()].reduce((sum, s) => sum + s.tables.size, 0)} tables
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-1">
        {schemaList.length === 0 && (
          <div className="text-tower-muted text-xs p-3 text-center">No schema data — seed artifacts first</div>
        )}
        {schemaList.map((node) => (
          <div key={node.schema} className="mb-0.5">
            <button
              onClick={() => setExpandedSchema(expandedSchema === node.schema ? null : node.schema)}
              className="w-full flex items-center gap-2 px-2 py-1 text-xs hover:bg-tower-border/30 rounded"
            >
              <span className="text-warning">S</span>
              <span className="text-tower-text font-semibold flex-1 text-left">{node.schema}</span>
              <span className="text-tower-muted">{node.tables.size}</span>
              <span className={`text-tower-muted transition-transform text-[10px] ${expandedSchema === node.schema ? 'rotate-90' : ''}`}>▸</span>
            </button>

            {expandedSchema === node.schema && (
              <div className="ml-3 border-l border-tower-border/30 pl-2">
                {[...node.tables.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([tableName, columns]) => (
                  <div key={tableName}>
                    <button
                      onClick={() => setExpandedTable(expandedTable === tableName ? null : tableName)}
                      className="w-full flex items-center gap-2 px-1 py-0.5 text-xs hover:bg-tower-border/20 rounded"
                    >
                      <span className="text-info text-[10px]">T</span>
                      <span className="text-tower-accent font-mono text-left flex-1">{tableName}</span>
                      <span className="text-tower-muted text-[10px]">{columns.length}c</span>
                      <span className={`text-tower-muted transition-transform text-[10px] ${expandedTable === tableName ? 'rotate-90' : ''}`}>▸</span>
                    </button>
                    {expandedTable === tableName && columns.length > 0 && (
                      <div className="ml-4 pl-2 border-l border-tower-border/20">
                        {columns.map((col) => (
                          <div key={col} className="text-[10px] text-tower-muted py-0.5 flex items-center gap-1">
                            <span className="text-success/60">C</span>
                            <span className="font-mono">{col}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="px-2 py-1 border-t border-tower-border/50 text-[10px] text-tower-muted shrink-0">
        Schema inferred from artifact SQL table references
      </div>
    </div>
  );
}
